import { ref, toRaw } from 'vue'
import type { UserWithProfile } from '@/types/user'
import * as authService from '@/api/authService'
import { useUsersStore } from '@/store/modules/user'
import { useProfilesStore } from '@/store/modules/userProfile'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { SocialLogin } from '@capgo/capacitor-social-login'

const isLoading = ref(false)
const isLoggedIn = ref(false)

export function useAuth() {
  const usersStore = useUsersStore()
  const profilesStore = useProfilesStore()

  async function checkLogin(): Promise<UserWithProfile | null> {
    isLoading.value = true
    try {
      const userData = await authService.checkLogin()
      if (!userData) throw new Error('Not logged in')

      usersStore.user = userData

      profilesStore.setUserProfile({
        userId: userData.userId,
        username: userData.username,
        regions: userData.regions,
        displayCount: userData.displayCount,
        included: userData.included,
        excluded: userData.excluded,
        hiddenTemplates: userData.hiddenTemplates
      })

      isLoggedIn.value = true
      return userData
    } catch {
      isLoggedIn.value = false
      usersStore.resetUser()
      profilesStore.resetProfile()

      const localKey = 'userPreferences'
      const data = localStorage.getItem(localKey)

      if (data) {
        try {
          const parsed = JSON.parse(data)

          if (parsed.userId !== 0) {
            localStorage.removeItem(localKey)
          } else {
            profilesStore.setUserProfile(parsed)
          }
        } catch (err) {
          console.error('Failed to parse local userPreferences:', err)
          localStorage.removeItem(localKey)
        }
      }
      return null
    } finally {
      isLoading.value = false
    }
  }
  async function clearSocialSessions() {
    if (Capacitor.isNativePlatform()) {
      try {
        await Promise.allSettled([
          SocialLogin.logout({ provider: 'google' }),
          SocialLogin.logout({ provider: 'apple' })
        ])
      } catch (err) {
        console.warn('[useAuth] Social logout failed', err)
      }
    }
  }

  async function logout() {
    await authService.logout()
    isLoggedIn.value = false
    usersStore.resetUser()
    profilesStore.resetProfile()
    const localKey = 'userPreferences'
    localStorage.removeItem(localKey)
    await clearSocialSessions()
    if (Capacitor.isNativePlatform()) {
      await Preferences.remove({ key: 'jwt' })
    }
  }


  let _pollTimer: ReturnType<typeof setInterval> | null = null

  function startPeriodicCheck(intervalMs = 3 * 60 * 1000) {
    if (_pollTimer !== null) return
    _pollTimer = setInterval(async () => {
      const prevLoggedIn = isLoggedIn.value
      const prevProfile = prevLoggedIn ? { ...toRaw(profilesStore.profile) } : null

      const userData = await checkLogin()

      if (prevLoggedIn && !userData) {
        // Session was killed externally — state already cleared by checkLogin
        return
      }

      if (userData && prevProfile) {
        // Sync if server preferences differ (cross-session update)
        const changed =
          JSON.stringify(userData.regions) !== JSON.stringify(prevProfile.regions) ||
          JSON.stringify(userData.included) !== JSON.stringify(prevProfile.included) ||
          JSON.stringify(userData.excluded) !== JSON.stringify(prevProfile.excluded) ||
          JSON.stringify(userData.hiddenTemplates) !== JSON.stringify(prevProfile.hiddenTemplates) ||
          userData.displayCount !== prevProfile.displayCount

        if (changed) {
          profilesStore.setUserProfile({
            userId: userData.userId,
            username: userData.username,
            regions: userData.regions,
            displayCount: userData.displayCount,
            included: userData.included,
            excluded: userData.excluded,
            hiddenTemplates: userData.hiddenTemplates
          })
        }
      }
    }, intervalMs)
  }

  function stopPeriodicCheck() {
    if (_pollTimer !== null) {
      clearInterval(_pollTimer)
      _pollTimer = null
    }
  }

  return {
    isLoading,
    isLoggedIn,
    checkLogin,
    logout,
    clearSocialSessions,
    startPeriodicCheck,
    stopPeriodicCheck,
    qrLogin: authService.qrLogin,
    requestCode: authService.requestCode,
    verifyCode: authService.verifyCode,
    loginWithGoogle: authService.loginWithGoogle,
    signupGoogle: authService.signupGoogle,
    linkWithGoogle: authService.linkWithGoogle,
    loginWithApple: authService.loginWithApple,
    signupApple: authService.signupApple,
    linkWithApple: authService.linkWithApple,
    getSessions: authService.getSessions,
    logoutSession: authService.logoutSession,
    logoutAllDevices: authService.logoutAllDevices
  }
}